import { I18n } from '@calendly/i18n';
import { PremiumIcon, DollarSign } from '@calendly/ui/components/icons';
import { colorTextStandard } from '@calendly/ui/theme';
import classNames from 'classnames';
import { forwardRef, useState, useLayoutEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { moveToCheckout } from 'actions/billing/subscription_modal';
import { CallToActionButton } from 'components/navigation/call_to_action';
import { currentStageSelector, currentTierSelector, setAnalyticsLocation, BILLING_ENTRYPOINTS } from 'features/billing';
import { useActivationQ3Flag } from 'features/empty_state';
import { TRACKING_EVENT_NAMES } from 'hooks/analytics_events';
import { useTrack } from 'hooks/analytics_events/useTrack';
import { isAdminOrOwnerSelector } from 'selectors/current_user';
import { COLLAPSE_FADEOUT_MS, NavigationIconContainer, NavigationLabel } from './elements';
import { useLeftNavigationState } from './hooks';
const BILLING_STAGE_PAID = 'paid';
const isNavMinimizedCss = "i15m6g2n";
const isNavCollapsingCss = "il4whid";
/* Same layout as all nav items - exceptional styling (border/background) only */
const upgradeCTAButtonCss = "u1wlgyg5";
const UpgradeCTAButton = forwardRef((props, ref) => {
  const {
    isNavMinimized,
    isNavCollapsing,
    ...restProps
  } = props;
  const isMinimizedLayout = isNavMinimized && !isNavCollapsing;
  return <CallToActionButton ref={ref} {...restProps} className={classNames(upgradeCTAButtonCss, {
    [isNavCollapsingCss]: isNavCollapsing,
    [isNavMinimizedCss]: isMinimizedLayout
  }, props.className)} />;
});
const getUpgradeText = billingStage => {
  if (billingStage === BILLING_STAGE_PAID) {
    return I18n.t('publisher.personal_settings.navigation.actions.view_subscriptions');
  }
  return I18n.t('publisher.left_navigation.upgrade_cta.caption');
};
export const UpgradeCTA = () => {
  const dispatch = useDispatch();
  const track = useTrack();
  const isActivationQ3FlagEnabled = useActivationQ3Flag();
  const leftNavigationState = useLeftNavigationState();
  const billingStage = useSelector(currentStageSelector);
  const billingTier = useSelector(currentTierSelector);
  const isAdminOrOwner = useSelector(isAdminOrOwnerSelector);
  const {
    pathname
  } = useLocation();
  const isNavMinimized = leftNavigationState === 'phone-collapsed' || leftNavigationState === 'minimized';
  const [isNavCollapsing, setIsNavCollapsing] = useState(false);

  /* useLayoutEffect so we set isNavCollapsing true before paint; otherwise first paint shows minimized layout and no fade-out */
  useLayoutEffect(() => {
    if (!isNavMinimized) {
      setIsNavCollapsing(false);
      return undefined;
    }
    setIsNavCollapsing(true);
    const t = setTimeout(() => setIsNavCollapsing(false), COLLAPSE_FADEOUT_MS);
    return () => clearTimeout(t);
  }, [isNavMinimized]);
  if (!isAdminOrOwner) return null;
  if (billingTier === 'enterprise') return null;
  const upgradeText = getUpgradeText(billingStage);
  const IconComponent = isActivationQ3FlagEnabled ? DollarSign : PremiumIcon;
  const onButtonClick = () => {
    dispatch(setAnalyticsLocation(BILLING_ENTRYPOINTS.LEFT_NAV));
    track({
      name: TRACKING_EVENT_NAMES.MODAL_INTERACTION,
      properties: {
        action: 'opening',
        location: 'left_nav',
        modal_name: 'upgrade_plan',
        path: pathname
      }
    });
    dispatch(moveToCheckout({
      analyticCheckoutFlowLocation: 'upgrade_prompt_left_nav'
    }));
  };
  return <UpgradeCTAButton onClick={() => onButtonClick()} mini tooltip={{
    text: isNavMinimized ? upgradeText : null,
    placement: 'right'
  }} isNavMinimized={isNavMinimized} isNavCollapsing={isNavCollapsing}>
      <NavigationIconContainer>
        <IconComponent color={colorTextStandard} />
      </NavigationIconContainer>
      <NavigationLabel>{upgradeText}</NavigationLabel>
    </UpgradeCTAButton>;
};

require("./upgrade_cta.wyw-in-js.css!=!../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./upgrade_cta.js");